import React, { useMemo, useState } from 'react';
import { ClientPersonRow, mergeUsersWithReps, RepSortKey, sortRepsBy } from '../lib/selectors';
import { ClientUsersData, RangeRep, TimeRange } from '../types/superadmin';
import { useClientUsers, ClientUsersState } from '../hooks/useClientUsers';
import { usePagination } from '../hooks/usePagination';
import { EmptyState, ErrorState, LoadingState, MetricBar, RepCell, ScoreCell } from './primitives';
import { rangeLabel } from './RangeControl';
import { fmtInt, initialsOf, timeAgo } from '../lib/format';
import AddClientUserForm from './AddClientUserForm';
import Pagination from './Pagination';
import RepSeriesPanel from './RepSeriesPanel';
import UserRowActions from './UserRowActions';
import SeatCount, { TRIAL_ACCESS_LABEL } from './SeatCount';

/**
 * One table for everyone at a client: the LIVE roster (read from the client's
 * own database, managed here for trial clients) joined to each person's call
 * and training stats for the selected range (computed live by backend-tenant).
 * Each row carries the roster columns (role, status, last login, account
 * actions) and the stat columns; expanding a row shows the by-day charts.
 *
 * Adding and removing users is restricted to clients with trial access — they
 * have unlimited seats, so no subscription seat has to be bought or released.
 * For every other client the roster is still shown, read-only, with the reason.
 */

const PEOPLE_PAGE_SIZE = 25;

const STAT_COLUMNS: ReadonlyArray<{ key: RepSortKey; label: string }> = [
  { key: 'sessions', label: 'Sessions' },
  { key: 'minutes', label: 'Minutes' },
  { key: 'objFaqScore', label: 'Obj/FAQ' },
  { key: 'landmineScore', label: 'Landmine' },
  { key: 'competitorScore', label: 'Competitor' },
  { key: 'playbookScore', label: 'Playbook' },
  { key: 'callQualityScore', label: 'Call quality' },
  { key: 'trainingQualityScore', label: 'Training quality' },
  { key: 'trainingCompletion', label: 'Training completion' }
];

// Expand/#, person, role, status, last login, one per stat, actions.
const TABLE_COLS = 5 + STAT_COLUMNS.length + 1;

/** Seat usage line, labelled Trial Access for trial clients (unlimited seats). */
function SeatSummary({ data }: { data: ClientUsersData }) {
  if (data.totalUserSeats === null) {
    return data.trialAccessEnabled ? (
      <span className="chip pos">{TRIAL_ACCESS_LABEL}</span>
    ) : (
      <span className="chip">No seat information</span>
    );
  }
  return (
    <SeatCount
      used={data.usedUserSeats}
      total={data.totalUserSeats}
      isTrial={data.trialAccessEnabled}
      suffix=" seats used"
    />
  );
}

function StatCell({ row, statKey }: { row: ClientPersonRow; statKey: RepSortKey }) {
  if (statKey === 'sessions') return <span className="metric-num">{fmtInt(row.sessions)}</span>;
  if (statKey === 'minutes') {
    return (
      <span className="metric-num">
        {fmtInt(row.minutes)}
        <span className="u">min</span>
      </span>
    );
  }
  if (statKey === 'callQualityScore') return <MetricBar value={row.callQualityScore} />;
  if (statKey === 'trainingQualityScore' || statKey === 'trainingCompletion') {
    return row.trainingSessions > 0 ? <MetricBar value={row[statKey]} /> : <span className="caption">—</span>;
  }
  return <ScoreCell value={row[statKey]} />;
}

/** Role, status and last login / last session cells; a removed person has only a status. */
function RosterCells({ row }: { row: ClientPersonRow }) {
  const user = row.user;
  if (!user) {
    return (
      <>
        <td />
        <td>
          <span className="chip">removed</span>
        </td>
        <td />
      </>
    );
  }
  return (
    <>
      <td>
        <span className={'chip ' + (user.role === 'admin' ? 'info' : '')}>{user.role}</span>
      </td>
      <td>
        <span className={'chip ' + (user.isActive ? 'pos' : 'neg')}>{user.isActive ? 'active' : 'deactivated'}</span>
      </td>
      <td className="nowrap">
        <div>
          <span className="last-seen-label">login</span> {timeAgo(user.lastLogin)}
        </div>
        <div>
          <span className="last-seen-label">session</span> {timeAgo(user?.lastSessionAt)}
        </div>
      </td>
    </>
  );
}

function PersonRow({
  row,
  index,
  isOpen,
  onToggleOpen,
  range,
  users
}: {
  row: ClientPersonRow;
  index: number;
  isOpen: boolean;
  onToggleOpen: () => void;
  range: TimeRange;
  users: ClientUsersState;
}) {
  return (
    <>
      <tr className="clickable" onClick={onToggleOpen}>
        <td>
          <button
            type="button"
            className="row-expander mono"
            aria-expanded={isOpen}
            aria-label={`${isOpen ? 'Hide' : 'Show'} details for ${row.name}`}
            onClick={(event) => {
              event.stopPropagation();
              onToggleOpen();
            }}
          >
            <span className={'caret' + (isOpen ? ' open' : '')}>▸</span>
            {String(index + 1).padStart(2, '0')}
          </button>
        </td>
        <td>
          <RepCell initials={initialsOf(row.name)} name={row.name} role={row.email || undefined} />
        </td>
        <RosterCells row={row} />
        {STAT_COLUMNS.map((column) => (
          <td key={column.key} className="nowrap">
            <StatCell row={row} statKey={column.key} />
          </td>
        ))}
        {/* Buttons act on the account; they must not also toggle the row open. */}
        <td className="nowrap" onClick={(event) => event.stopPropagation()}>
          {row.user && (
            <UserRowActions
              user={row.user}
              canManage={users.data?.trialAccessEnabled === true}
              busy={users.busy}
              onToggle={users.toggleUser}
              onDelete={users.deleteUser}
              onResend={users.resendWelcome}
            />
          )}
        </td>
      </tr>
      {isOpen && (
        <tr className="rep-detail-row">
          <td colSpan={TABLE_COLS}>
            <RepSeriesPanel rep={row} range={range} />
          </td>
        </tr>
      )}
    </>
  );
}

export default function ClientUsersPanel({
  tenantId,
  clientName,
  reps,
  range
}: {
  tenantId: string;
  clientName: string;
  /** Range stats per rep (client detail endpoint), joined onto the live roster. */
  reps: RangeRep[];
  range: TimeRange;
}) {
  const users = useClientUsers(tenantId, clientName);
  const [showForm, setShowForm] = useState(false);
  const [sortKey, setSortKey] = useState<RepSortKey>('callQualityScore');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const rows = useMemo(
    () => sortRepsBy(mergeUsersWithReps(users.data?.users ?? [], reps, tenantId), sortKey, sortDir),
    [users.data, reps, tenantId, sortKey, sortDir]
  );
  const pagination = usePagination(rows, PEOPLE_PAGE_SIZE, `${sortKey}:${sortDir}`);

  const canManage = users.data?.trialAccessEnabled === true;
  const loadedUsers = users.data?.users?.length ?? 0;

  const setSort = (key: RepSortKey) => {
    if (sortKey === key) setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    else {
      setSortKey(key);
      setSortDir('desc');
    }
  };
  const arrow = (key: RepSortKey) => (sortKey === key ? (sortDir === 'desc' ? '↓' : '↑') : '↕');

  return (
    <section className="section">
      <div className="section-head">
        <div>
          <h3>Users, {rangeLabel(range)}</h3>
          <p className="desc">
            Live roster from the client's own database, with each person's call quality score (live-call
            handling + playbook), training quality score and training completion for the period. Click a row for
            activity by day. Stat columns are sortable.
          </p>
        </div>
        <div className="right">
          {users.data && <SeatSummary data={users.data} />}
          {canManage && !showForm && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setShowForm(true);
                users.clearMessages();
              }}
            >
              Add user
            </button>
          )}
        </div>
      </div>

      {users.loading && <LoadingState label="Loading users…" />}
      {!users.loading && users.loadError && <ErrorState message={users.loadError} />}

      {!users.loading && !users.loadError && users.data && (
        <>
          {!canManage && (
            <div className="panel-note">
              This client does not have trial access, so its users are managed from its own admin
              portal against its purchased seats. Enable trial access to manage users from here.
            </div>
          )}
          {users.totalUsers > loadedUsers && (
            <div className="panel-note">
              Showing the {loadedUsers} most recently added of {users.totalUsers} users.
            </div>
          )}

          {users.notice && <div className="panel-note pos">{users.notice}</div>}
          {users.actionError && <div className="panel-note neg">{users.actionError}</div>}

          {showForm && canManage && (
            <AddClientUserForm
              clientName={clientName}
              submitting={users.creating}
              onSubmit={async (payload) => {
                if (await users.createUser(payload)) setShowForm(false);
              }}
              onCancel={() => setShowForm(false)}
            />
          )}

          <div className="tbl tbl-scroll">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 60 }}>#</th>
                  <th>User</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last login / session</th>
                  {STAT_COLUMNS.map((column) => (
                    <th
                      key={column.key}
                      className={'sortable' + (sortKey === column.key ? ' sorted' : '')}
                      onClick={() => setSort(column.key)}
                      aria-sort={sortKey === column.key ? (sortDir === 'desc' ? 'descending' : 'ascending') : 'none'}
                    >
                      {column.label} <span className="sort-arr">{arrow(column.key)}</span>
                    </th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {pagination.pageItems.map((row, i) => (
                  <PersonRow
                    key={row.user?.id ?? row.userId}
                    row={row}
                    index={pagination.firstIndex - 1 + i}
                    isOpen={expandedId === row.userId}
                    onToggleOpen={() => setExpandedId((cur) => (cur === row.userId ? null : row.userId))}
                    range={range}
                    users={users}
                  />
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={TABLE_COLS}>
                      <EmptyState>This client has no users yet.</EmptyState>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <Pagination state={pagination} itemLabel="users" />
        </>
      )}
    </section>
  );
}
